import vehicleService from "../../service/vehicle.service";
import {
  mergeDriverVehicleTrailerIds,
  mergeVehicleTrailers,
  normalizeTrailerIds,
} from "./reboque-trailer-utils";

function getVehiclePointer(allDataOrPointer) {
  if (!allDataOrPointer) return null;
  return allDataOrPointer.vehicle !== undefined
    ? allDataOrPointer.vehicle
    : allDataOrPointer;
}

/**
 * Extrai vehicleId do ponteiro fino (ou snapshot legado) em allData.vehicle.
 */
export function getDriverVehicleId(allDataOrVehicle) {
  return String(getVehiclePointer(allDataOrVehicle)?.vehicleId ?? "").trim();
}

/**
 * Fallback legado: snapshot embutido sem documento na coleção vehicle.
 */
export function mapLegacyDriverVehicleSnapshot(pointer) {
  if (!pointer?.vehiclePlate) return null;
  return {
    id: pointer.vehicleId || undefined,
    vehiclePlate: pointer.vehiclePlate,
    bodyworkPlate: pointer.bodyworkPlate || "",
    bodyworkType: pointer.bodyworkType || "",
    capacidadeKG: pointer.capacidadeKG || "",
    capacidadeM3: pointer.capacidadeM3 || "",
    cpfoucnpj: pointer.cpfoucnpj || "",
    descricaoVeiculo: pointer.descricaoVeiculo || "",
    nomeProprietario: pointer.nomeProprietario || "",
    pesoTara: pointer.pesoTara || "",
    trailerIds: normalizeTrailerIds(pointer.trailerIds),
    reboques: Array.isArray(pointer.reboques) ? pointer.reboques : [],
    renavam: pointer.renavam || "",
    rntrc: pointer.rntrc || "",
    tipoProprietario: pointer.tipoProprietario || "",
    tipoRNTRC: pointer.tipoRNTRC || "",
    ufVeiculo: pointer.ufVeiculo || "",
    vehicleImg: pointer.vehicleImg || "",
    vehicleType: pointer.vehicleType || "",
    veiculoProprio: pointer.veiculoProprio ?? false,
    documentCRLV: pointer.documentCRLV || "",
    documentAnttFrontImg: pointer.documentAnttFrontImg || "",
  };
}

/**
 * Une trailerIds (e reboques embutidos) do veículo com o legado do motorista.
 */
export function mergeVehicleWithDriverLegacy(vehicle, allDataOrPointer) {
  if (!vehicle || typeof vehicle !== "object") return vehicle;

  const pointer = getVehiclePointer(allDataOrPointer);
  const mergedTrailerIds = mergeDriverVehicleTrailerIds(
    vehicle.trailerIds,
    pointer?.trailerIds,
  );

  const vehicleEmbedded = Array.isArray(vehicle.reboques)
    ? vehicle.reboques
    : [];
  const pointerEmbedded = Array.isArray(pointer?.reboques)
    ? pointer.reboques
    : [];
  const vehicleEmbeddedIds = new Set(
    vehicleEmbedded
      .map((item) => String(item?.id || "").trim())
      .filter(Boolean),
  );

  return {
    ...vehicle,
    trailerIds: mergedTrailerIds,
    reboques: [
      ...vehicleEmbedded,
      ...pointerEmbedded.filter((item) => {
        const id = String(item?.id || "").trim();
        return id ? !vehicleEmbeddedIds.has(id) : Boolean(item?.placaReboque);
      }),
    ],
  };
}

/**
 * Enriquece o veículo com reboques unificados (trailerIds + legado) para exibição.
 * Não promove nem altera a persistência.
 */
export async function enrichVehicleTrailers(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return vehicle;
  const ids = normalizeTrailerIds(vehicle.trailerIds);
  if (ids.length === 0) {
    return {
      ...vehicle,
      reboques: Array.isArray(vehicle.reboques) ? vehicle.reboques : [],
    };
  }
  try {
    const docs = await vehicleService.getByIds(ids);
    const merged = mergeVehicleTrailers(vehicle, docs);
    return {
      ...vehicle,
      reboques: merged.map((item) => item.data),
    };
  } catch (error) {
    console.warn("[enrichVehicleTrailers] falha ao resolver trailerIds", error);
    return vehicle;
  }
}

async function enrichWithDriverLegacy(vehicle, allData) {
  if (!vehicle) return null;
  return enrichVehicleTrailers(mergeVehicleWithDriverLegacy(vehicle, allData));
}

/**
 * Resolve veículo do motorista pela coleção vehicle (fonte de verdade).
 * Une trailerIds do veículo com legado do motorista (allData.vehicle.trailerIds).
 */
export async function resolveVehicleForDriver(driverUid, allData) {
  const uid = String(driverUid || "").trim();
  if (uid) {
    try {
      const vehicle = await vehicleService.getByDriverUid(uid);
      if (vehicle) return enrichWithDriverLegacy(vehicle, allData);
    } catch (error) {
      console.warn(
        "[resolveVehicleForDriver] falha getByDriverUid",
        uid,
        error,
      );
    }
  }

  const pointer = getVehiclePointer(allData);
  const vehicleId = getDriverVehicleId(pointer);
  if (vehicleId) {
    try {
      const response = await vehicleService.getById(vehicleId);
      const vehicle = response?.data?.data ?? response?.data ?? null;
      if (vehicle) return enrichWithDriverLegacy(vehicle, allData);
    } catch (error) {
      console.warn(
        "[resolveVehicleForDriver] falha getById",
        vehicleId,
        error,
      );
    }
  }

  return mapLegacyDriverVehicleSnapshot(pointer);
}

/**
 * Resolve veículos para vários motoristas (batch na API).
 * @returns {Promise<Map<string, object|null>>}
 */
export async function resolveVehiclesForDrivers(driverUids) {
  const map = await vehicleService.getByDriverUids(driverUids);
  const entries = await Promise.all(
    Object.entries(map || {}).map(async ([key, vehicle]) => [
      key,
      vehicle ? await enrichVehicleTrailers(vehicle) : null,
    ]),
  );
  return new Map(entries);
}

/**
 * Batch por vehicleIds → Map(id → vehicle).
 */
export async function resolveVehiclesByIds(vehicleIds) {
  const list = await vehicleService.getByIds(vehicleIds);
  const enriched = await Promise.all(
    (list || []).map((item) => enrichVehicleTrailers(item)),
  );
  return new Map(enriched.map((item) => [String(item.id), item]));
}
